import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { providePrimeNG } from 'primeng/config';

import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, UrlSerializer, withPreloading, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { AppRoutes } from '@app/app.routes';
import { CanDeactivateGuard } from '@app/helpers/guards/can-deactivate-guard';
import { DashboardGuard } from '@app/helpers/guards/dashboard-guard';
import { FlagGuard } from '@app/helpers/guards/flag-dialog/flag-guard';
import { ErrorInterceptor } from '@app/helpers/interceptors/error.interceptor';
import { JwtInterceptor } from '@app/helpers/interceptors/jwt.interceptor';
import { LowerCaseUrlSerializer } from '@app/helpers/lower-case-url.serializer';
import { LOCATION_TOKEN, UPDATE_SERVICE_AUTO_DELAY } from '@app/injection-tokens';
import { LoginGuard } from '@app/interface/login-guard';
import { AuthenticationService } from '@app/services/authentication/authentication.service';
import { CrudService } from '@app/services/crud/crud.service';
import { MapService } from '@app/services/maps/map.service';
import { NotificationService } from '@app/services/notification/notification-service';
import { UpdateService } from '@app/services/update.service';
import { UserExperienceService } from '@app/services/user-experience.service';
import { createErrorHandler, SentryErrorHandler, TraceService } from '@sentry/angular';
import { SMapService } from '@summit/maps';
import { SummitUserExperienceService } from '@summit/models';
import { SummitAuthService, SummitCrudService, SummitNotificationService } from '@summit/services';
import { COPY_TO_CLIPBOARD_HANDLER } from '@summit/ui';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { EboPreloadingStrategy } from './ebo-preloading-strategy';
import { EboTheme } from './theme/ebo-prime-theme';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      AppRoutes,
      withRouterConfig({
        onSameUrlNavigation: 'reload'
      }),
      withPreloading(EboPreloadingStrategy)
    ),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideAnimations(), //Need Prime 21 to remove
    providePrimeNG({
      ripple: true,
      overlayOptions: {
        mode: 'overlay',
        responsive: {
          // style?: any;                                     // Style of component in given breakpoint or media query
          // styleClass?: string;                             // Style class of component in given breakpoint or media query
          // contentStyle?: any;                              // Style of content in given breakpoint or media query
          // contentStyleClass?: string;                      // Style class of content in given breakpoint or media query
          // breakpoint?: string;                             // Breakpoint required to show component in modal mode. Exp: '640px', '10rem' etc.
          // media?: string;                                  // Media query required to show component in modal mode. Exp: '@media screen and (max-width: 640px)', '@media screen and (min-width: 640px) and (max-width: 900px)' etc.
          direction: 'center' // Direction in which the component will be displayed in modal mode.
        }
      },
      theme: {
        preset: EboTheme,
        options: {
          darkModeSelector: '.dark'
        }
      },
      //keeping stuff under 999 since that is where the time picker pops
      zIndex: {
        modal: 900, // dialog, sidebar
        overlay: 800, // dropdown, overlaypanel
        menu: 800, // overlay menus
        tooltip: 900 // tooltip
      }
    }),
    // provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production
    }),
    CanDeactivateGuard,
    {
      provide: COPY_TO_CLIPBOARD_HANDLER,
      useFactory(messageService: MessageService) {
        return function (text) {
          const message: ToastMessageOptions = {
            severity: 'success',
            summary: 'Copied',
            detail: `${text.length < 20 ? text : ''} copied to clipboard!`,
            //id?: any;
            //key?: string;
            life: 3000,
            sticky: undefined,
            closable: true,
            //data: any,
            icon: 'content_copy'
            //contentStyleClass: string,
            //styleClass: string
          };
          messageService.add(message);
        };
      },
      deps: [MessageService]
    },
    // PrimeNG
    DialogService,
    MessageService,
    // Guards
    DashboardGuard,
    LoginGuard,
    FlagGuard,
    //Internal Services
    AuthenticationService,
    //Services
    // CalendarService,
    // UserExperienceService,
    DatePipe,
    { provide: UPDATE_SERVICE_AUTO_DELAY, useValue: 1 * 1000 },
    { provide: LOCATION_TOKEN, useValue: window.location },
    { provide: 'BASE_URL', useFactory: getBaseUrl },
    //{ provide: 'environment', useValue: environment },
    {
      provide: SummitAuthService,
      useFactory: (auth: AuthenticationService) => auth,
      deps: [AuthenticationService]
    },
    {
      provide: SummitUserExperienceService,
      useFactory: (uxService: UserExperienceService) => uxService,
      deps: [UserExperienceService]
    },
    {
      provide: SMapService,
      useFactory: (mapService: MapService) => mapService,
      deps: [MapService]
    },
    {
      provide: SummitCrudService,
      useFactory: (crudService: CrudService) => crudService,
      deps: [CrudService]
    },
    {
      provide: SummitNotificationService,
      useFactory: (notificationService: NotificationService) => notificationService,
      deps: [NotificationService]
    },
    {
      provide: ErrorHandler,
      useFactory: () => createErrorHandler({
        showDialog: false,
        logErrors: true
      })
    },
    {
      provide: TraceService,
      deps: [Router]
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: () => () => {
    //     // noop
    //   },
    //   deps: [Sentry.TraceService],
    //   multi: true
    // },
    provideAppInitializer(() => {
      const updateService = inject(UpdateService);
      // Initialize UpdateService to ensure it's loaded in production
      updateService.logStart();
    }),
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer
    }
  ]
};

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}
